// components/StatusModal.jsx
import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  CircularProgress,
} from '@mui/material';

const StatusModal = ({
  open,
  onClose,
  selectedDba,
  newStatus = '',
  onStatusChange,
  notes = '',
  onNotesChange,
  sku = '',
  onSkuChange,
  gateway = '',               // new prop
  onGatewayChange,            // new prop
  showNotes = false,
  showSku = false,
  showGateway = false,        // renamed for clarity
  onConfirm,
  statusOptions = [],
  processing = false,
  title = "Change DBA Status",
  description = null,
}) => {
  const defaultDescription = `Update the status for ${selectedDba?.name || 'this DBA'}. Select a new status below.`;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      sx={{
        '& .MuiDialog-paper': {
          borderRadius: 2,
          boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
          p: 2,
        },
      }}
    >
      <DialogTitle
        sx={{
          fontSize: '1.5rem',
          fontWeight: 600,
          color: '#154A8A',
          pb: 1,
          borderBottom: '1px solid',
          borderColor: 'grey.200',
        }}
      >
        {title}
      </DialogTitle>

      <DialogContent sx={{ pt: 3, pb: 2 }}>
        <DialogContentText sx={{ mb: 3, color: 'grey.700' }}>
          {description || defaultDescription}
        </DialogContentText>

        {/* Status Select */}
        <FormControl fullWidth variant="outlined" sx={{ mb: 3 }}>
          <InputLabel id="status-select-label">Change the DBA status to</InputLabel>
          <Select
            labelId="status-select-label"
            value={newStatus}
            onChange={onStatusChange}
            label="Change the DBA status to"
            sx={{ bgcolor: 'white', borderRadius: 1 }}
            disabled={processing}
          >
            {statusOptions.map((status) => (
              <MenuItem key={status.id} value={status.id}>
                {status.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* SKU Field - single line */}
        {showSku && (
          <TextField
            label="SKU"
            value={sku}
            onChange={onSkuChange}
            variant="outlined"
            fullWidth
            placeholder="Enter SKU..."
            disabled={processing}
            sx={{ mb: showNotes || showGateway ? 3 : 0 }}
          />
        )}

        {/* Gateway Field - single line */}
        {showGateway && (
          <TextField
            label="Gateway"
            value={gateway}
            onChange={onGatewayChange}
            variant="outlined"
            fullWidth
            placeholder="Enter Gateway..."
            disabled={processing}
            sx={{ mb: showNotes ? 3 : 0 }}
          />
        )}

        {/* Notes Field - multiline */}
        {showNotes && (
          <TextField
            label="Notes / Reason (optional)"
            multiline
            rows={4}
            value={notes}
            onChange={onNotesChange}
            variant="outlined"
            fullWidth
            placeholder="Explain why you're changing the status..."
            disabled={processing}
          />
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
        <Button
          onClick={onClose}
          variant="outlined"
          color="secondary"
          disabled={processing}
          sx={{
            borderColor: 'grey.400',
            color: 'grey.700',
            '&:hover': { borderColor: 'grey.600', bgcolor: 'grey.50' },
          }}
        >
          Cancel
        </Button>

        <Button
          onClick={onConfirm}
          variant="contained"
          color="primary"
          disabled={!newStatus || processing}
          startIcon={processing ? <CircularProgress size={20} color="inherit" /> : null}
          sx={{
            bgcolor: '#154A8A',
            '&:hover': { bgcolor: '#0d3a6a' },
            '&:disabled': { bgcolor: '#154A8A', opacity: 0.5 },
          }}
        >
          Change Status
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default StatusModal;